import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '@/components/Section';
import { DynamicIcon } from '@/components/DynamicIcon';
import { usePublicContent } from './use-public-content';
import { useSectionCopy } from './use-section-copy';

/** The five-step path, rendered as a connected vertical timeline. */
export function Process() {
  const { t, i18n } = useTranslation();
  const { steps, isLoading } = usePublicContent();
  const isDe = i18n.resolvedLanguage !== 'en';
  const copy = useSectionCopy('process', {
    eyebrow: t('process.eyebrow'),
    title: t('process.title'),
  });

  return (
    <Section id="ablauf" eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} maxWidth="md">
      {isLoading ? (
        <Stack spacing={2}>
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} variant="rounded" height={92} />
          ))}
        </Stack>
      ) : (
        <Stack>
          {steps.map((step, index) => (
            <Stack key={step.id} direction="row" spacing={{ xs: 2.5, md: 4 }}>
              <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 800,
                    fontSize: 17,
                  }}
                >
                  {index + 1}
                </Box>
                {index < steps.length - 1 && (
                  <Box sx={{ flex: 1, width: '1px', bgcolor: 'divider', my: 1 }} />
                )}
              </Stack>

              <Box sx={{ pb: index < steps.length - 1 ? { xs: 4, md: 5 } : 0, pt: 0.75 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
                  <DynamicIcon name={step.icon} sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="h4" component="h3">
                    {isDe ? step.titleDe : step.titleEn}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
                  {isDe ? step.bodyDe : step.bodyEn}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Section>
  );
}
